import { useState, useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useToast } from "@/components/ui/use-toast";
import { Module, CONTRACTOR_CARD_FILE_INPUT_ID } from "@/constants";
import { useFileDragAndDrop } from "@/hooks/useFileDragAndDrop";
import { triggerFileInputClick } from "@/lib/fileManagerUtils";
import { buildFileUrl } from "@/lib/utils/buildFileUrl";
import { useBase64FileEncoder } from "@/lib/file-storage/hooks";
import {
  useFilePreview,
  type PendingPreviewFile,
} from "@/components/file-preview/hooks";

interface UseCardFileUploadParams {
  cardId?: string;
  cardS3Key?: string;
  queryKey: Module;
  patchCard: (
    cardId: string,
    data: { content: string; filename: string },
  ) => Promise<unknown>;
}

export const useCardFileUpload = ({
  cardId,
  cardS3Key,
  queryKey,
  patchCard,
}: UseCardFileUploadParams) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileManager = useBase64FileEncoder();

  const [pendingFile, setPendingFile] = useState<PendingPreviewFile | null>(
    null,
  );
  const [isUploading, setIsUploading] = useState(false);
  const { preview, openBase64Preview, openPreviewDialog, closePreview } =
    useFilePreview({
      onError: (message: string) => {
        toast({
          title: t("contractorCards.errors.noFileToView"),
          description: message,
          variant: "destructive",
        });
      },
    });

  const updateMutation = useMutation({
    mutationFn: async (data: { content: string; filename: string }) => {
      if (!cardId) {
        throw new Error("Contractor card ID is required");
      }
      return patchCard(cardId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      setPendingFile(null);
      setIsUploading(false);
      toast({ title: t("contractorCards.success.upload") });
    },
    onError: () => {
      setIsUploading(false);
      toast({
        title: t("contractorCards.errors.uploadFailed"),
        variant: "destructive",
      });
    },
  });

  const processFileMutation = useMutation({
    mutationFn: async (file: File) => {
      const attachment = await fileManager.encode(file);
      return {
        content: attachment.content,
        filename: attachment.filename,
        mimetype: file.type,
      };
    },
    onSuccess: (data) => {
      setPendingFile(data);
    },
    onError: () => {
      toast({
        title: t("contractorCards.errors.uploadFailed"),
        variant: "destructive",
      });
    },
  });

  const { isDragging, dragHandlers } = useFileDragAndDrop({
    onDrop: async (files) => {
      const file = files[0];
      if (file) {
        await processFileMutation.mutateAsync(file);
      }
    },
  });

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      await processFileMutation.mutateAsync(file);
      event.target.value = "";
    },
    [processFileMutation],
  );

  const handleViewFile = useCallback(() => {
    if (pendingFile) {
      openBase64Preview(pendingFile);
      return;
    }

    if (cardS3Key) {
      openPreviewDialog(buildFileUrl(cardS3Key));
      return;
    }

    toast({
      title: t("contractorCards.errors.noFileToView"),
      variant: "destructive",
    });
  }, [pendingFile, cardS3Key, openBase64Preview, openPreviewDialog, toast, t]);

  const handleConfirmSend = useCallback(async () => {
    if (!pendingFile) return;
    setIsUploading(true);
    await updateMutation.mutateAsync({
      content: pendingFile.content,
      filename: pendingFile.filename,
    });
  }, [pendingFile, updateMutation]);

  const clearPendingFile = useCallback(() => {
    setPendingFile(null);
  }, []);

  const handleFileInputClick = useCallback(() => {
    triggerFileInputClick(CONTRACTOR_CARD_FILE_INPUT_ID);
  }, []);

  return {
    pendingFile,
    isUploading,
    isDragging,
    dragHandlers,
    handleFileChange,
    handleViewFile,
    handleConfirmSend,
    clearPendingFile,
    handleFileInputClick,
    fileInputId: CONTRACTOR_CARD_FILE_INPUT_ID,
    previewDialog: preview,
    handleClosePreview: closePreview,
  };
};
